/**
 * Authentication Store — Donor Portal
 *
 * Manages user authentication state, tokens, and user information.
 * Note: auth store will not be fully effective until the donor-specific
 * auth integration is completed.
 *
 * Token refresh:
 *   • Proactive: schedules a refresh ~60 s before accessToken expiry
 *   • Reactive:  401 interceptor in api.service.ts calls refreshAccessToken()
 */
import { defineStore } from 'pinia';
import { isAxiosError } from 'axios';
import type { BaseUser, LoginChallengeResponse, CognitoLoginResponse, RespondToChallengeResponse, TotpInitiateResponse, MfaStatus } from '@tms-phoenix/tms-types';
import { AUTH_SESSION_EXP_COOKIE } from '@tms-phoenix/tms-types';
import { hasIdleTimeoutElapsed, clearLastActivityTimestamp } from '@tms-phoenix/ui-vue';
import type { DonorLoginChallenge } from '@/modules/auth/types';
import { loginWithCredentials, logoutSession } from '@/modules/auth/services/auth.service';
import { useLocaleStore } from '@/stores/locale.store';
import { getApiClient, getApiErrorMessage, logAuditEvent } from '@/core/services';
import { API_ROUTES } from '@/core/config';
import { setAuthContext, clearAuthContext } from '@/core/config/app.config';
import { router } from '@/router/instance';

// ── Proactive refresh timer ─────────────────────────────────────────────────
let _refreshTimerId: ReturnType<typeof setTimeout> | null = null;

/** In-flight refresh promise mutex — prevents concurrent refresh calls. */
let _refreshPromise: Promise<boolean> | null = null;

// ── Transient auth-failure retry (SV-563) ───────────────────────────────────
let _sessionRetryTimerId: ReturnType<typeof setTimeout> | null = null;
let _sessionRetryAttempt = 0;

/** Statuses that mean the session itself is invalid — the only ones that should log the user out. */
const SESSION_INVALID_STATUSES = new Set([400, 401, 403]);

// Keep the retry policy below in sync with tms-app-soho and tms-app-product auth.store.ts.

/** Backoff for 5xx / network errors — covers a Wi-Fi roam or a rolling deploy (~3 min total). */
const SESSION_RETRY_DELAYS_MS = [5_000, 15_000, 45_000, 120_000];
/** /auth/refresh blocks for up to its 15-minute rate-limit window; never wait longer than that. */
const MAX_RETRY_AFTER_MS = 15 * 60_000;
/** ±25 % spread so browsers that failed together don't all retry at the same instant. */
const RETRY_JITTER = 0.25;

/** Spread proactive refreshes over this window so tabs sharing an expiry don't all refresh at once. */
const REFRESH_SPREAD_SEC = 30;
/**
 * After a 429 with Retry-After, no /auth/refresh before this time (epoch ms) — enforced in
 * refreshAccessToken() so every caller (401 interceptor, guard, timers) honours it.
 */
let _refreshNotBefore = 0;

/** What _scheduleSessionRetry() scheduled — `null` when retries are exhausted. */
type ScheduledSessionRetry = { retryAttempt: number; retryInMs: number } | null;

/** A 429's Retry-After in ms (capped at MAX_RETRY_AFTER_MS), or `null` when absent or not a 429. */
function retryAfterMs(err: unknown): number | null {
  if (!isAxiosError(err) || err.response?.status !== 429) return null;
  const retryAfterSec = Number(err.response.headers?.['retry-after']);
  return Number.isFinite(retryAfterSec) && retryAfterSec > 0 ? Math.min(retryAfterSec * 1000, MAX_RETRY_AFTER_MS) : null;
}

/**
 * SV-563: how long to wait before retrying /auth/session-check or /auth/refresh after a
 * transient failure (429 rate limit, 5xx, network error). The user stays signed in either way.
 *
 * @param err     The error thrown by the failed request (usually an AxiosError).
 * @param attempt 0 for the first retry, incremented on each consecutive failure.
 * @returns Delay in ms before retrying, or `null` to stop retrying.
 */
function getSessionRetryDelayMs(err: unknown, attempt: number): number | null {
  // Give up after 4 attempts — the user stays signed in; the 401 → refresh path still applies.
  if (attempt >= SESSION_RETRY_DELAYS_MS.length) return null;

  // 429 with Retry-After: the server says exactly when a retry can succeed. Only add
  // jitter *after* that point — retrying earlier would just be rejected again.
  const retryAfter = retryAfterMs(err);
  if (retryAfter !== null) {
    return Math.round(retryAfter + Math.random() * 2_000);
  }

  // 5xx, network errors, or a 429 without Retry-After: increasing gaps with jitter.
  const base = SESSION_RETRY_DELAYS_MS[attempt];
  return Math.round(base * (1 - RETRY_JITTER + Math.random() * 2 * RETRY_JITTER));
}

/** HTTP status for log lines, or 'network' when the request never got a response. */
function describeAuthFailure(err: unknown): string {
  return String((isAxiosError(err) && err.response?.status) || 'network');
}

// ── Session-expiry marker cookie (SV-563) ───────────────────────────────────
// Keep in sync with tms-app-soho and tms-app-product auth.store.ts.

/** Another tab's refresh only counts if it moved the expiry forward by more than this. */
const MARKER_NEWER_THRESHOLD_SEC = 30;
/**
 * The marker is page-writable, so reject values further ahead than any real access token can
 * last (Cognito's maximum access-token validity is 24 h) — a tampered value then behaves like
 * a missing marker (the /auth/session-check fallback) instead of skewing refresh timing.
 */
const MAX_MARKER_AHEAD_SEC = 24 * 60 * 60;
/** Whether this page has ever seen a marker — a missing one only means "session ended" if so. */
let _sessionMarkerSeen = false;
let _visibilityListenerRegistered = false;

/**
 * Read the readable `tms_session_exp` marker the backend sets alongside the HttpOnly access
 * cookie (same lifetime). Returns its expiry in epoch seconds, or `null` when it's absent,
 * malformed, already past, or implausibly far ahead. A UI timing hint only — never used for
 * authorization.
 */
function readSessionExpCookie(): number | null {
  const prefix = `${AUTH_SESSION_EXP_COOKIE}=`;
  const entry = document.cookie.split('; ').find((c) => c.startsWith(prefix));
  const exp = Number(entry?.slice(prefix.length));
  const nowSec = Math.floor(Date.now() / 1000);
  if (!Number.isInteger(exp) || exp <= nowSec || exp > nowSec + MAX_MARKER_AHEAD_SEC) return null;
  _sessionMarkerSeen = true;
  return exp;
}

/** Decode a JWT payload without signature verification (client-side only). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    let payload = token.split('.')[1];
    if (!payload) return null;
    // Restore base64url → base64 and pad to a multiple of 4
    payload = payload.replace(/-/g, '+').replace(/_/g, '/');
    while (payload.length % 4 !== 0) payload += '=';
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function decodeJwtExp(token: string): number | null {
  const json = decodeJwtPayload(token);
  return json && typeof json.exp === 'number' ? json.exp : null;
}

interface LoginCredentials {
  email: string;
  password: string;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

interface AuthState {
  user: BaseUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  /**
   * SV-563: access-token expiry (epoch seconds) reported by GET /auth/session-check on page
   * reload, when no in-memory accessToken exists. In-memory only — never persisted.
   */
  sessionExpiresAt: number | null;
  isLoading: boolean;
  error: string | null;
  personId: string | null;
  /** User's time zone from login/reset response (e.g. 'America/New_York'). */
  timeZone: string | null;
  /** Pending Cognito auth challenge (e.g. NEW_PASSWORD_REQUIRED, SOFTWARE_TOKEN_MFA). */
  challengeData: LoginChallengeResponse | null;
  /** Email captured at login time; used to populate BaseUser after password reset. */
  pendingEmail: string | null;
  /** True when login succeeded but MFA has not been configured yet. */
  mfaSetupRequired: boolean;
  /** Which MFA challenge type is pending after login. */
  mfaChallengeType: 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP' | null;
  /** QR code data from POST /mfa/setup/totp/initiate. */
  totpQrData: TotpInitiateResponse | null;
  /** Temporary access token from SELECT_MFA_TYPE challenge, used to authenticate MFA setup calls. */
  mfaSetupAccessToken: string | null;
  /** Error code returned by the API on login failure (e.g. 'USER_DISABLED'). */
  loginErrorCode: string | null;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    accessToken: null,
    refreshToken: null,
    sessionExpiresAt: null,
    isLoading: false,
    error: null,
    personId: null,
    timeZone: null,
    challengeData: null,
    pendingEmail: null,
    mfaSetupRequired: false,
    mfaChallengeType: null,
    totpQrData: null,
    mfaSetupAccessToken: null,
    loginErrorCode: null,
  }),

  getters: {
    // PHX-1180: no longer gated on accessToken — the real session lives in an
    // httpOnly cookie the SPA can't read. A missing accessToken after a page
    // reload does NOT mean the user is logged out (see initAuthConfig()).
    isAuthenticated: (state): boolean => !!state.user,
    /**
     * SV-563: access-token expiry (epoch seconds). Prefers the in-memory token (set after
     * login/refresh in this page load) over the session-check value from page reload.
     */
    sessionExp: (state): number | null =>
      state.accessToken ? decodeJwtExp(state.accessToken) : state.sessionExpiresAt,
    /** SV-563: logged in AND the access-token expiry is known (login, refresh, or session-check). */
    hasActiveSession(): boolean {
      return this.isAuthenticated && this.sessionExp !== null;
    },
    userRole: (state): string | undefined => state.user?.role,
    userName: (state): string | undefined => state.user?.name || state.user?.email,
    userEmail: (state): string | undefined => state.user?.email,
  },

  actions: {
    async login(credentials: LoginCredentials): Promise<boolean> {
      this.isLoading = true;
      this.error = null;
      this.loginErrorCode = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;
      let auditResourceId: string | undefined;

      const beforeLoginAuthenticated = this.isAuthenticated;
      const beforeLoginPersonId = this.personId;
      const beforeLoginChallenge = this.challengeData?.challengeName ?? null;

      try {
        const data = await loginWithCredentials(credentials);

        if (data.type === 'challenge') {
          // Store challenge data so ResetPasswordView / MfaChallengeView can complete the flow
          this.challengeData = data.challenge;
          this.pendingEmail = credentials.email;
          if (
            data.challenge.challengeName === 'SOFTWARE_TOKEN_MFA' ||
            data.challenge.challengeName === 'EMAIL_OTP'
          ) {
            this.mfaChallengeType = data.challenge.challengeName as 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP';
          }
          // Capture setup token issued alongside SELECT_MFA_TYPE for MFA enrollment calls
          this.mfaSetupAccessToken = (data as DonorLoginChallenge).access_token ?? null;

          // First-time MFA setup: /login returns full tokens with SELECT_MFA_TYPE.
          if (
            data.challenge.challengeName === 'SELECT_MFA_TYPE' &&
            (data as DonorLoginChallenge).refresh_token &&
            (data as DonorLoginChallenge).user
          ) {
            const challengeData = data as DonorLoginChallenge;
            const loginUser = challengeData.user!;
            if (loginUser.person_id) {
              const now = new Date().toISOString();
              const user: BaseUser = {
                id: loginUser.username,
                email: credentials.email,
                name: credentials.email,
                role: 'user',
                createdAt: now,
                updatedAt: now,
              };
              this.setAuth(
                { accessToken: challengeData.access_token!, refreshToken: challengeData.refresh_token ?? '' },
                user,
                loginUser.person_id,
              );
              this.timeZone = loginUser.time_zone ?? null;
              this.mfaSetupRequired = true;
            }
          }

          // mfaSetupRequired: skip MFA method-selection screen by auto-selecting EMAIL_OTP.
          // Fires for both token-bearing and token-less SELECT_MFA_TYPE responses.
          if (
            data.challenge.challengeName === 'SELECT_MFA_TYPE' &&
            (data as DonorLoginChallenge).mfaSetupRequired
          ) {
            const api = getApiClient();
            const username = data.challenge.challengeParameters?.USER_ID_FOR_SRP ?? credentials.email;
            const autoRespondResponse = await api.post<CognitoLoginResponse>(
              `${API_ROUTES.ONBOARDING}/auth/respond-challenge`,
              {
                challengeName: 'SELECT_MFA_TYPE',
                session: data.challenge.session,
                username,
                answer: 'EMAIL_OTP',
              },
            );
            const autoRespondData = autoRespondResponse.data;
            if (autoRespondData.type === 'challenge') {
              this.challengeData = autoRespondData.challenge;
              if (autoRespondData.challenge.challengeName === 'EMAIL_OTP') {
                this.mfaChallengeType = 'EMAIL_OTP';
              }
            }
          }
          // Required MFA pool: MFA_SETUP challenge or mfaSetupRequired flag
          if (
            data.challenge.challengeName === 'MFA_SETUP' ||
            (data as DonorLoginChallenge).mfaSetupRequired
          ) {
            this.mfaSetupRequired = true;
          }

          this.isLoading = false;
          return false;
        }

        if (data.type !== 'authenticated') {
          this.error = getApiErrorMessage(null, 'apiErrors.unexpectedAuthResponse');
          return false;
        }

        const { access_token, refresh_token, user: loginUser } = data.tokens;

        // person_id is required for every donor API call — fail before
        // touching any auth state so a stale session is never partially updated.
        if (!loginUser.person_id) {
          this.error = getApiErrorMessage(null, 'apiErrors.missingPersonId');
          return false;
        }

        const now = new Date().toISOString();

        const user: BaseUser = {
          id: loginUser.username,
          email: credentials.email,
          name: credentials.email,
          role: 'user',
          createdAt: now,
          updatedAt: now,
        };
        this.setAuth(
          { accessToken: access_token, refreshToken: refresh_token ?? '' },
          user,
          loginUser.person_id ?? null,
        );
        this.timeZone = loginUser.time_zone ?? null;
        this.mfaSetupRequired = data.mfaSetupRequired === true;

        auditStatus = 'SUCCESS';
        auditResourceId = this.personId ?? undefined;

        try {
          const localeStore = useLocaleStore();
          await localeStore.loadFullBundle();
          await localeStore.fetchLanguages();
        } catch {
          // Non-fatal: embedded fallback translations still work
        }

        return true;
      } catch (err) {
        this.error = getApiErrorMessage(err, 'apiErrors.loginFailed');
        auditStatus = 'FAILURE';
        auditErrorCode = 'LOGIN_FAILED';
        auditErrorMessage = this.error;
        if (isAxiosError(err)) {
          const apiError = err.response?.data?.error;
          if (apiError && typeof apiError === 'object' && 'code' in apiError) {
            this.loginErrorCode = (apiError as { code: string }).code;
            auditErrorCode = this.loginErrorCode ?? 'LOGIN_FAILED';
          }
        }
        return false;
      } finally {
        this.isLoading = false;
        try {
          const afterLoginChallenge = this.challengeData?.challengeName ?? null;
          const oldValue = {
            isAuthenticated: beforeLoginAuthenticated,
            personId: beforeLoginPersonId,
            challengeName: beforeLoginChallenge,
          };
          const newValue = {
            isAuthenticated: this.isAuthenticated,
            personId: this.personId,
            challengeName: afterLoginChallenge,
          };
          logAuditEvent({
            eventCategory: 'AUTH_DONOR_PORTAL',
            eventType: 'LOGIN',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: auditResourceId,
            oldValue,
            newValue,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
            isPhiAccess: false,
          });
        } catch {
          // Audit must never block login
        }
      }
    },

    /**
     * Complete the NEW_PASSWORD_REQUIRED challenge.
     *
     * POST /onboarding/api/v1/auth/respond-challenge
     * On success, follows the same post-login flow as a normal login:
     * sets tokens, person_id, user, and loads the locale bundle.
     */
    async completeNewPasswordChallenge(newPassword: string): Promise<boolean> {
      if (!this.challengeData) {
        this.error = 'No pending challenge. Please log in again.';
        return false;
      }

      this.isLoading = true;
      this.error = null;

      const beforeSetPasswordAuthenticated = this.isAuthenticated;
      const beforeSetPasswordChallenge = this.challengeData?.challengeName ?? null;

      try {
        const api = getApiClient();
        const { challengeName, session, challengeParameters } = this.challengeData;
        // USER_ID_FOR_SRP is the Cognito username — may be email or UUID.
        const username = challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
        if (!username) {
          this.challengeData = null;
          this.error = 'Session data is incomplete. Please log in again.';
          return false;
        }

        const resetResponse = await api.post<CognitoLoginResponse | RespondToChallengeResponse>(
          `${API_ROUTES.ONBOARDING}/auth/respond-challenge`,
          { challengeName, session, username, newPassword },
        );

        const resetData = resetResponse.data;

        // The reset-password endpoint may return:
        //   1. Wrapped format: { type: 'authenticated', tokens: { ... } }
        //   2. MFA challenge:  { type: 'challenge', challenge: { challengeName, ... } }
        //   3. Flat format:    { access_token, refresh_token, user, ... }
        let accessToken: string;
        let refreshToken: string;
        let loginUser: RespondToChallengeResponse['user'];
        let pendingMfaSetupRequired = false;

        if ('type' in resetData && resetData.type === 'authenticated') {
          ({ access_token: accessToken, user: loginUser } = resetData.tokens);
          refreshToken = resetData.tokens.refresh_token ?? '';
          pendingMfaSetupRequired = resetData.mfaSetupRequired === true;
        } else if ('type' in resetData && resetData.type === 'challenge') {
          // MFA challenge returned after password reset — mirror the normal login flow
          const challengeResp = resetData as DonorLoginChallenge;
          this.challengeData = challengeResp.challenge;
          if (
            challengeResp.challenge.challengeName === 'SOFTWARE_TOKEN_MFA' ||
            challengeResp.challenge.challengeName === 'EMAIL_OTP'
          ) {
            this.mfaChallengeType = challengeResp.challenge.challengeName as 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP';
          }
          if (challengeResp.challenge.challengeName === 'SELECT_MFA_TYPE') {
            this.mfaSetupAccessToken = challengeResp.access_token ?? null;
            if (challengeResp.access_token && challengeResp.refresh_token && challengeResp.user?.person_id) {
              const now = new Date().toISOString();
              const mfaEmail = this.pendingEmail ?? challengeResp.user.username;
              const mfaUser: BaseUser = {
                id: challengeResp.user.username,
                email: mfaEmail,
                name: mfaEmail,
                role: 'user',
                createdAt: now,
                updatedAt: now,
              };
              this.setAuth(
                { accessToken: challengeResp.access_token, refreshToken: challengeResp.refresh_token },
                mfaUser,
                challengeResp.user.person_id,
              );
              this.timeZone = challengeResp.user.time_zone ?? null;
            }
            this.mfaSetupRequired = true;
          }
          if (
            challengeResp.challenge.challengeName === 'MFA_SETUP' ||
            (challengeResp as DonorLoginChallenge).mfaSetupRequired
          ) {
            this.mfaSetupRequired = true;
          }
          return false;
        } else if ('access_token' in resetData) {
          const flat = resetData as RespondToChallengeResponse;
          accessToken = flat.access_token;
          refreshToken = flat.refresh_token ?? '';
          loginUser = flat.user;
        } else {
          throw new Error('Unexpected response format after password reset');
        }

        // Clear challenge data now that the flow is complete
        this.challengeData = null;

        // person_id is required for every donor API call
        if (!loginUser.person_id) {
          this.error = getApiErrorMessage(null, 'apiErrors.missingPersonId');
          return false;
        }

        const now = new Date().toISOString();
        const email = this.pendingEmail ?? loginUser.username;
        this.pendingEmail = null;

        const user: BaseUser = {
          id: loginUser.username,
          email,
          name: email,
          role: 'user',
          createdAt: now,
          updatedAt: now,
        };

        this.setAuth(
          { accessToken, refreshToken },
          user,
          loginUser.person_id ?? null,
        );
        this.timeZone = loginUser.time_zone ?? null;
        this.mfaSetupRequired = pendingMfaSetupRequired;

        const oldValue = {
          isAuthenticated: beforeSetPasswordAuthenticated,
          challengeName: beforeSetPasswordChallenge,
        };
        const newValue = {
          isAuthenticated: this.isAuthenticated,
          challengeName: null,
        };
        try {
          logAuditEvent({
            eventCategory: 'AUTH_DONOR_PORTAL',
            eventType: 'SET_PASSWORD',
            eventStatus: 'SUCCESS',
            resourceType: 'users',
            resourceId: this.personId ?? undefined,
            oldValue,
            newValue,
            errorCode: undefined,
            errorMessage: undefined,
            isPhiAccess: false,
          });
        } catch {
          // Audit must never block the flow
        }

        try {
          const localeStore = useLocaleStore();
          await localeStore.loadFullBundle();
          await localeStore.fetchLanguages();
        } catch {
          // Non-fatal: embedded fallback translations still work
        }

        return true;
      } catch (err) {
        // SESSION_EXPIRED: challenge session expired (Cognito TTL = 3 min).
        // Clear stale challenge data so the view redirects to login.
        const axiosErr = err as { response?: { status?: number; data?: { error?: { code?: string; message?: string } } } };
        if (axiosErr.response?.status === 401) {
          const code = axiosErr.response.data?.error?.code;
          if (code === 'SESSION_EXPIRED') {
            this.challengeData = null;
            this.error =
              axiosErr.response.data?.error?.message ?? 'Session expired. Please log in again.';
            try {
              logAuditEvent({
                eventCategory: 'AUTH_DONOR_PORTAL',
                eventType: 'SET_PASSWORD',
                eventStatus: 'FAILURE',
                resourceType: 'users',
                resourceId: undefined,
                oldValue: { isAuthenticated: beforeSetPasswordAuthenticated, challengeName: beforeSetPasswordChallenge },
                newValue: { isAuthenticated: this.isAuthenticated, challengeName: null },
                errorCode: 'SESSION_EXPIRED',
                errorMessage: this.error,
                isPhiAccess: false,
              });
            } catch { /* Audit must never block the flow */ }
            return false;
          }
        }
        this.error = getApiErrorMessage(err, 'auth.passwordResetFailed');
        try {
          logAuditEvent({
            eventCategory: 'AUTH_DONOR_PORTAL',
            eventType: 'SET_PASSWORD',
            eventStatus: 'FAILURE',
            resourceType: 'users',
            resourceId: undefined,
            oldValue: {
              isAuthenticated: beforeSetPasswordAuthenticated,
              challengeName: beforeSetPasswordChallenge,
            },
            newValue: {
              isAuthenticated: this.isAuthenticated,
              challengeName: this.challengeData?.challengeName ?? null,
            },
            errorCode: 'SET_PASSWORD_FAILED',
            errorMessage: this.error,
            isPhiAccess: false,
          });
        } catch {
          // Audit must never block the flow
        }
        return false;
      } finally {
        this.isLoading = false;
      }
    },

    // ── MFA actions ────────────────────────────────────────────────────────

    async respondMfaChallenge(code: string): Promise<boolean> {
      if (!this.challengeData) {
        this.error = 'No active MFA challenge. Please log in again.';
        return false;
      }

      const { challengeName, session, challengeParameters } = this.challengeData;
      const username = challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
      if (!username) {
        this.error = 'Session data is incomplete. Please log in again.';
        return false;
      }

      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        // loginType: 'donor' is injected automatically by the API service interceptor
        const response = await api.post<RespondToChallengeResponse>(
          `${API_ROUTES.ONBOARDING}/auth/respond-challenge`,
          { challengeName, session, username, code },
        );

        this.challengeData = null;
        this.mfaChallengeType = null;

        const loginUser = response.data.user;
        if (!loginUser.person_id) {
          this.error = getApiErrorMessage(null, 'apiErrors.missingPersonId');
          auditErrorCode = 'MISSING_PERSON_ID';
          auditErrorMessage = this.error ?? undefined;
          return false;
        }

        const now = new Date().toISOString();
        const email = this.pendingEmail ?? loginUser.username;
        const user: BaseUser = {
          id: loginUser.username,
          email,
          name: email,
          role: 'user',
          createdAt: now,
          updatedAt: now,
        };
        this.setAuth(
          { accessToken: response.data.access_token, refreshToken: response.data.refresh_token ?? '' },
          user,
          loginUser.person_id,
        );
        this.timeZone = loginUser.time_zone ?? null;
        this.pendingEmail = null;
        this.mfaSetupRequired = (response.data as { mfaSetupRequired?: boolean }).mfaSetupRequired === true;

        try {
          const { useLocaleStore } = await import('@/stores/locale.store');
          const localeStore = useLocaleStore();
          await localeStore.loadFullBundle();
          await localeStore.fetchLanguages();
        } catch {
          // Non-fatal: embedded fallback translations still work
        }

        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          const errCode = err.response?.data?.error?.code;
          if (errCode === 'SESSION_EXPIRED' || errCode === 'MFA_SESSION_EXPIRED') {
            this.challengeData = null;
            this.mfaChallengeType = null;
          }
          this.error = err.response?.data?.error?.message ?? err.message ?? 'Verification failed';
          auditErrorCode = errCode ?? 'MFA_CHALLENGE_FAILED';
          auditErrorMessage = this.error ?? undefined;
        } else {
          this.error = err instanceof Error ? err.message : 'Verification failed';
          auditErrorCode = 'MFA_CHALLENGE_FAILED';
          auditErrorMessage = this.error ?? undefined;
        }
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_CHALLENGE_VERIFY',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.personId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async fetchMfaMethods(): Promise<('authenticator' | 'email')[]> {
      try {
        const api = getApiClient();
        const response = await api.get<{ methods: string[] }>(`${API_ROUTES.ONBOARDING}/mfa/methods`);
        const methods: string[] = response.data?.methods ?? [];
        const mapped: ('authenticator' | 'email')[] = [];
        if (methods.includes('TOTP')) mapped.push('authenticator');
        if (methods.includes('EMAIL_OTP')) mapped.push('email');
        return mapped.length > 0 ? mapped : ['authenticator', 'email'];
      } catch {
        return ['authenticator', 'email'];
      }
    },

    async initiateTotpSetup(): Promise<boolean> {
      this.isLoading = true;
      this.error = null;
      this.totpQrData = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        const response = await api.post<TotpInitiateResponse>(
          `${API_ROUTES.ONBOARDING}/mfa/setup/totp/initiate`,
        );
        this.totpQrData = response.data;
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'MFA_SETUP_TOTP_INITIATE_FAILED';
          this.error = err.response?.data?.error?.message ?? err.message;
        } else {
          this.error = err instanceof Error ? err.message : 'Failed to initiate TOTP setup';
          auditErrorCode = 'MFA_SETUP_TOTP_INITIATE_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_TOTP_INITIATE',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.personId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async verifyTotpSetup(code: string): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        await api.post(`${API_ROUTES.ONBOARDING}/mfa/setup/totp/verify`, { code });
        this.totpQrData = null;
        this.mfaSetupRequired = false;
        this.challengeData = null;
        this.mfaSetupAccessToken = null;
        // Re-apply auth context so config.personId is populated for immediate
        // navigation to the dashboard after MFA setup completes.
        setAuthContext({ personId: this.personId ?? undefined });
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          const errCode = err.response?.data?.error?.code;
          if (errCode === 'TOTP_SETUP_FAILED') this.totpQrData = null;
          this.error = err.response?.data?.error?.message ?? err.message;
          auditErrorCode = errCode ?? 'MFA_SETUP_TOTP_VERIFY_FAILED';
        } else {
          this.error = err instanceof Error ? err.message : 'TOTP verification failed';
          auditErrorCode = 'MFA_SETUP_TOTP_VERIFY_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_TOTP_VERIFY',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.personId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async setupEmailOtp(): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        await api.post(`${API_ROUTES.ONBOARDING}/mfa/setup/email/initiate`);
        this.mfaSetupRequired = false;
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'MFA_SETUP_EMAIL_INITIATE_FAILED';
          this.error = err.response?.data?.error?.message ?? err.message;
        } else {
          this.error = err instanceof Error ? err.message : 'Failed to send email OTP';
          auditErrorCode = 'MFA_SETUP_EMAIL_INITIATE_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_EMAIL_INITIATE',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.personId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async verifyEmailOtp(code: string): Promise<boolean> {
      this.isLoading = true;
      this.error = null;

      let auditStatus: 'SUCCESS' | 'FAILURE' = 'FAILURE';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      try {
        const api = getApiClient();
        await api.post(`${API_ROUTES.ONBOARDING}/mfa/setup/email/verify`, { code });
        this.mfaSetupRequired = false;
        this.mfaSetupAccessToken = null;
        // Re-apply auth context so config.personId is populated for immediate
        // navigation to the dashboard after MFA setup completes.
        setAuthContext({ personId: this.personId ?? undefined });
        auditStatus = 'SUCCESS';
        return true;
      } catch (err) {
        if (isAxiosError(err)) {
          auditErrorCode = err.response?.data?.error?.code ?? 'MFA_SETUP_EMAIL_VERIFY_FAILED';
          this.error = err.response?.data?.error?.message ?? err.message;
        } else {
          this.error = err instanceof Error ? err.message : 'Email verification failed';
          auditErrorCode = 'MFA_SETUP_EMAIL_VERIFY_FAILED';
        }
        auditErrorMessage = this.error ?? undefined;
        return false;
      } finally {
        this.isLoading = false;
        try {
          logAuditEvent({
            eventCategory: 'AUTH',
            eventType: 'MFA_SETUP_EMAIL_VERIFY',
            eventStatus: auditStatus,
            resourceType: 'users',
            resourceId: this.personId ?? undefined,
            isPhiAccess: false,
            errorCode: auditErrorCode,
            errorMessage: auditErrorMessage,
          });
        } catch {
          // Audit must never block the flow
        }
      }
    },

    async getMfaStatus(): Promise<MfaStatus | null> {
      try {
        const api = getApiClient();
        const response = await api.get<MfaStatus>(`${API_ROUTES.ONBOARDING}/mfa/status`);
        return response.data;
      } catch {
        return null;
      }
    },

    // ── In-auth MFA setup (Required MFA pools — MFA_SETUP challenge, no token yet) ──

    async initiateInAuthTotpSetup(): Promise<boolean> {
      if (!this.challengeData) return false;
      this.isLoading = true;
      this.error = null;
      this.totpQrData = null;
      const session = this.challengeData.session;
      const username = this.challengeData.challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
      try {
        const api = getApiClient();
        // loginType: 'donor' is injected by the request interceptor
        const response = await api.post<{ session: string; secretCode: string; otpauthUri: string; qrCodeDataUrl: string }>(
          `${API_ROUTES.ONBOARDING}/auth/mfa-setup/totp/initiate`,
          { session, username },
        );
        this.challengeData = { ...this.challengeData, session: response.data.session };
        this.totpQrData = { qrCodeDataUrl: response.data.qrCodeDataUrl, otpauthUri: response.data.otpauthUri };
        return true;
      } catch (err) {
        this.error = isAxiosError(err) ? (err.response?.data?.error?.message ?? err.message) : (err instanceof Error ? err.message : 'Failed to initiate TOTP setup');
        return false;
      } finally { this.isLoading = false; }
    },

    async completeInAuthTotpSetup(code: string): Promise<boolean> {
      if (!this.challengeData) return false;
      this.isLoading = true;
      this.error = null;
      const session = this.challengeData.session;
      const username = this.challengeData.challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
      try {
        const api = getApiClient();
        const response = await api.post<CognitoLoginResponse>(
          `${API_ROUTES.ONBOARDING}/auth/mfa-setup/totp/complete`,
          { session, username, code },
        );
        const data = response.data;
        if ('type' in data && data.type === 'authenticated') {
          this.challengeData = null;
          this.totpQrData = null;
          const { access_token, refresh_token, user: loginUser } = data.tokens;
          if (!loginUser.person_id) { this.error = 'Missing person_id'; return false; }
          const now = new Date().toISOString();
          const email = this.pendingEmail ?? loginUser.username;
          const baseUser: BaseUser = { id: loginUser.username, email, name: email, role: 'user', createdAt: now, updatedAt: now };
          this.setAuth({ accessToken: access_token, refreshToken: refresh_token ?? '' }, baseUser, loginUser.person_id);
          this.timeZone = loginUser.time_zone ?? null;
          this.pendingEmail = null;
          this.mfaSetupRequired = false;
          this.mfaSetupAccessToken = null;
          setAuthContext({ personId: loginUser.person_id });
          return true;
        }
        if ('type' in data && data.type === 'challenge') {
          this.challengeData = data.challenge;
          if (data.challenge.challengeName === 'EMAIL_OTP') this.mfaChallengeType = 'EMAIL_OTP';
          return false;
        }
        return false;
      } catch (err) {
        if (isAxiosError(err)) {
          if (err.response?.data?.error?.code === 'TOTP_SETUP_FAILED') this.totpQrData = null;
          this.error = err.response?.data?.error?.message ?? err.message;
        } else { this.error = err instanceof Error ? err.message : 'TOTP verification failed'; }
        return false;
      } finally { this.isLoading = false; }
    },

    async completeInAuthEmailSetup(): Promise<boolean> {
      if (!this.challengeData) return false;
      this.isLoading = true;
      this.error = null;
      const session = this.challengeData.session;
      const username = this.challengeData.challengeParameters?.USER_ID_FOR_SRP ?? this.pendingEmail ?? '';
      try {
        const api = getApiClient();
        const response = await api.post<CognitoLoginResponse>(
          `${API_ROUTES.ONBOARDING}/auth/mfa-setup/email/complete`,
          { session, username },
        );
        const data = response.data;
        if ('type' in data && data.type === 'challenge' && data.challenge.challengeName === 'EMAIL_OTP') {
          this.challengeData = data.challenge;
          this.mfaChallengeType = 'EMAIL_OTP';
          return true;
        }
        if ('type' in data && data.type === 'authenticated') {
          this.challengeData = null;
          const { access_token, refresh_token, user: loginUser } = data.tokens;
          if (!loginUser.person_id) { this.error = 'Missing person_id'; return false; }
          const now = new Date().toISOString();
          const email = this.pendingEmail ?? loginUser.username;
          const baseUser: BaseUser = { id: loginUser.username, email, name: email, role: 'user', createdAt: now, updatedAt: now };
          this.setAuth({ accessToken: access_token, refreshToken: refresh_token ?? '' }, baseUser, loginUser.person_id);
          this.timeZone = loginUser.time_zone ?? null;
          this.pendingEmail = null;
          this.mfaSetupRequired = false;
          setAuthContext({ personId: loginUser.person_id });
          return true;
        }
        return false;
      } catch (err) {
        this.error = isAxiosError(err) ? (err.response?.data?.error?.message ?? err.message) : (err instanceof Error ? err.message : 'Failed to enable Email OTP');
        return false;
      } finally { this.isLoading = false; }
    },

    /**
     * @param options.navigate - When false, skips the `/login` push at the end
     *   (default true). Used by ActiveSessionView, which routes to `/login`
     *   itself (preserving the pending `?redirect=`) once the logout settles.
     * @param options.clearFirst - When true, wipes the local session BEFORE the
     *   server call (default false). Used by ActiveSessionView so the previous
     *   user's tokens are gone from the device immediately instead of after the
     *   request settles. The captured tokens are still sent to the server.
     * @param options.timeoutMs - Per-request timeout for the server call, so a
     *   slow or unreachable API can't hold the caller up for the default 30 s.
     */
    async logout(options: { navigate?: boolean; clearFirst?: boolean; timeoutMs?: number } = {}): Promise<void> {
      const { navigate = true, clearFirst = false, timeoutMs } = options;
      let auditStatus: 'SUCCESS' | 'FAILURE' = 'SUCCESS';
      let auditErrorCode: string | undefined;
      let auditErrorMessage: string | undefined;

      // Capture everything the server call and the audit event need — with
      // clearFirst the store is already empty by the time they run.
      const accessToken = this.accessToken;
      const personId = this.personId;
      const beforeLogout = {
        isAuthenticated: this.isAuthenticated,
        personId,
      };

      const clearLocalSession = (): void => {
        this.clearAuth();

        try {
          const localeStore = useLocaleStore();
          localeStore.resetOnLogout();
        } catch {
          // Non-fatal
        }

        // Clear all browser storage for this app session on logout.
        localStorage.clear();
        sessionStorage.clear();
      };

      if (clearFirst) clearLocalSession();

      try {
        await logoutSession(accessToken, timeoutMs);
      } catch (err) {
        auditStatus = 'FAILURE';
        auditErrorCode = 'LOGOUT_FAILED';
        auditErrorMessage = err instanceof Error ? err.message : 'Logout failed';
      }

      try {
        logAuditEvent({
          eventCategory: 'AUTH_DONOR_PORTAL',
          eventType: 'LOGOUT',
          eventStatus: auditStatus,
          resourceType: 'users',
          resourceId: personId ?? undefined,
          oldValue: beforeLogout,
          newValue: { isAuthenticated: false, personId: null },
          errorCode: auditErrorCode,
          errorMessage: auditErrorMessage,
          isPhiAccess: false,
        });
      } catch {
        // Audit must never block logout
      }

      if (!clearFirst) clearLocalSession();

      if (navigate) router.push('/login');
    },

    /**
     * Exchange the current refresh token for a new token pair.
     *
     * Called by:
     *   • The 401 response interceptor in api.service.ts (reactive)
     *   • The proactive timer scheduled in setAuth() (~60 s before expiry)
     *
     * Uses a shared in-flight mutex (_refreshPromise) so concurrent callers
     * dedupe to a single refresh operation and avoid token-rotation races.
     *
     * On success → stores new tokens and reschedules the proactive timer.
     * On failure → clears auth state and redirects to /login.
     */
    async refreshAccessToken(): Promise<boolean> {
      // SV-563: honour a 429's Retry-After for every caller, without touching the scheduled
      // retry (it fires at that time anyway). The user stays signed in meanwhile.
      if (Date.now() < _refreshNotBefore) return false;

      // Dedupe: if a refresh is already in flight, return its result
      if (_refreshPromise) return _refreshPromise;

      _refreshPromise = this._doRefresh();
      try {
        return await (_refreshPromise as Promise<boolean>);
      } finally {
        _refreshPromise = null;
      }
    },

    /** Internal refresh implementation (called via mutex wrapper). */
    async _doRefresh(): Promise<boolean> {
      if (!this.user?.id) {
        this.clearAuth();
        router.push('/login');
        return false;
      }

      try {
        const api = getApiClient();
        // PHX-1180: refreshToken is no longer persisted, so it's null after a
        // page reload — the backend falls back to the httpOnly tms_refresh_token cookie
        // in that case. Still send it when present (e.g. immediately post-login).
        const body: { username: string; refresh_token?: string } = { username: this.user.id };
        if (this.refreshToken) {
          body.refresh_token = this.refreshToken;
        }

        const { data } = await api.post<{ access_token: string; refresh_token?: string }>(
          `${API_ROUTES.ONBOARDING}/auth/refresh`,
          body,
        );

        this.accessToken = data.access_token;
        if (data.refresh_token) {
          this.refreshToken = data.refresh_token;
        }
        this._clearSessionRetry();
        _refreshNotBefore = 0;

        // Reschedule proactive refresh for the new access token
        this._scheduleProactiveRefresh();

        return true;
      } catch (err) {
        // SV-563: only an invalid session logs the user out. A 429 (rate limit), 5xx or
        // network error is transient — keep the user signed in and retry later.
        if (isAxiosError(err) && SESSION_INVALID_STATUSES.has(err.response?.status ?? 0)) {
          this.clearAuth();
          router.push('/login');
        } else {
          // A 429 says exactly how long to wait — hold every refresh until then (5xx/network
          // get no cooldown: a deploy can recover within seconds).
          const waitMs = retryAfterMs(err);
          if (waitMs !== null) _refreshNotBefore = Date.now() + waitMs;
          // Retry via the timer path so it adopts another tab's refresh instead of repeating it.
          this._scheduleSessionRetry(err, () => this._onRefreshTimer());
        }
        return false;
      }
    },

    /**
     * SV-563: confirm the httpOnly access cookie is still valid on page reload, without
     * spending a rate-limited /auth/refresh call.
     *
     * Fast path: the readable `tms_session_exp` marker cookie already says when the access
     * cookie expires — no request needed. Otherwise (marker missing):
     *
     * Endpoint: GET /onboarding/api/v1/auth/session-check → { expiresAt, username }
     *
     *   • 200 → remember the expiry and schedule the proactive refresh for ~60 s before it.
     *   • 401 → access cookie expired/missing — fall back to a single cookie-based refresh.
     *   • 429 / 5xx / network → keep the user signed in and retry later.
     */
    async validateSession(): Promise<boolean> {
      const markerExp = readSessionExpCookie();
      if (markerExp !== null) {
        this.sessionExpiresAt = markerExp;
        this._clearSessionRetry();
        this._scheduleProactiveRefresh();
        return true;
      }

      try {
        const api = getApiClient();
        const { data } = await api.get<{ expiresAt: number; username: string }>(
          `${API_ROUTES.ONBOARDING}/auth/session-check`,
        );

        this.sessionExpiresAt = data.expiresAt;
        this._clearSessionRetry();
        this._scheduleProactiveRefresh();
        return true;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 401) {
          return this.refreshAccessToken();
        }
        this._scheduleSessionRetry(err, () => this.validateSession());
        return true;
      }
    },

    /**
     * SV-563: schedule `retry` after a transient auth failure, per getSessionRetryDelayMs().
     * Logs status/attempt/delay only — never tokens or usernames.
     * Returns what was scheduled, or `null` when retries are exhausted.
     */
    _scheduleSessionRetry(err: unknown, retry: () => Promise<boolean>): ScheduledSessionRetry {
      if (_sessionRetryTimerId) clearTimeout(_sessionRetryTimerId);
      _sessionRetryTimerId = null;

      const failure = describeAuthFailure(err);
      const delayMs = getSessionRetryDelayMs(err, _sessionRetryAttempt);
      if (delayMs === null) {
        console.warn(`[auth.store] transient auth failure (${failure}) — retries exhausted; next 401 will refresh`);
        // Reset so a later, unrelated failure gets a fresh set of attempts. Nothing is
        // scheduled here, so this cannot loop.
        _sessionRetryAttempt = 0;
        return null;
      }

      _sessionRetryAttempt += 1;
      console.warn(
        `[auth.store] transient auth failure (${failure}) — retry ${_sessionRetryAttempt}/${SESSION_RETRY_DELAYS_MS.length} in ${Math.round(delayMs / 1000)}s`,
      );
      _sessionRetryTimerId = setTimeout(() => {
        _sessionRetryTimerId = null;
        void retry();
      }, delayMs);
      return { retryAttempt: _sessionRetryAttempt, retryInMs: delayMs };
    },

    /** SV-563: cancel any pending transient-failure retry and reset the attempt counter. */
    _clearSessionRetry() {
      if (_sessionRetryTimerId) clearTimeout(_sessionRetryTimerId);
      _sessionRetryTimerId = null;
      _sessionRetryAttempt = 0;
    },

    setAuth(tokens: AuthTokens, userData: BaseUser, personId?: string | null) {
      this.accessToken = tokens.accessToken;
      this.refreshToken = tokens.refreshToken;
      this.user = userData;
      if (personId !== undefined) {
        this.personId = personId;
      }
      setAuthContext({ personId: this.personId ?? undefined });

      // Schedule proactive token refresh ~60 s before the access token expires
      this._scheduleProactiveRefresh();
    },

    clearAuth() {
      this._clearRefreshTimer();
      this._clearSessionRetry();
      _refreshNotBefore = 0;
      this.accessToken = null;
      this.refreshToken = null;
      this.sessionExpiresAt = null;
      _sessionMarkerSeen = false;
      this.user = null;
      this.error = null;
      this.personId = null;
      this.timeZone = null;
      this.challengeData = null;
      this.pendingEmail = null;
      this.mfaSetupRequired = false;
      this.mfaChallengeType = null;
      this.totpQrData = null;
      this.mfaSetupAccessToken = null;
      clearAuthContext();

      // Reset the persisted idle-activity timestamp so the next login isn't
      // immediately flagged as idle-expired by a stale value.
      clearLastActivityTimestamp();
    },

    /**
     * Clears auth state and redirects to /login without calling the logout API.
     * Used when the session is already known to be invalid server-side (e.g. a
     * refresh-token retry that still 401s) — hitting /auth/logout would just 401 again.
     */
    forceLogout() {
      this.clearAuth();
      router.push('/login');
    },

    updateUser(userData: Partial<BaseUser>) {
      if (this.user) {
        this.user = { ...this.user, ...userData };
      }
    },

    hasRole(role: string): boolean {
      return this.userRole === role;
    },

    hasAnyRole(roles: string[]): boolean {
      return roles.some((role) => this.hasRole(role));
    },

    // ── Internal helpers ───────────────────────────────────────────────────

    /**
     * Schedule a proactive token refresh ~60 s before the access token expires.
     * Avoids 401 round-trips for long-lived sessions.
     */
    _scheduleProactiveRefresh() {
      this._clearRefreshTimer();

      // SV-563: in-memory token expiry, or the session-check expiry after a page reload
      const exp = this.sessionExp;
      if (!exp) return;

      const nowSec = Math.floor(Date.now() / 1000);
      // Refresh 60–90 s before expiry, minimum 10 s from now. SV-563: the random spread stops
      // tabs that share an expiry (via the marker) from all refreshing in the same second —
      // the first one refreshes and the others adopt its marker. Never fire inside a 429
      // cooldown (+1 s margin): refreshAccessToken() would reject it and nothing would retry.
      const delaySec = Math.max(
        exp - nowSec - 60 - Math.random() * REFRESH_SPREAD_SEC,
        10,
        (_refreshNotBefore - Date.now()) / 1000 + 1,
      );

      _refreshTimerId = setTimeout(() => {
        void this._onRefreshTimer();
      }, delaySec * 1000);
    },

    /**
     * SV-563: proactive-refresh timer callback. If another tab already refreshed (the shared
     * marker cookie moved forward), adopt its expiry instead of spending a /auth/refresh call.
     */
    async _onRefreshTimer(): Promise<boolean> {
      if (this._adoptNewerSessionMarker()) return true;
      return this.refreshAccessToken();
    },

    /**
     * SV-563: if the marker cookie shows a later expiry than this tab knows (another tab
     * refreshed), adopt it and reschedule. Returns true when adopted.
     */
    _adoptNewerSessionMarker(): boolean {
      const markerExp = readSessionExpCookie();
      const knownExp = this.sessionExp;
      if (markerExp === null) return false;
      if (knownExp !== null && markerExp <= knownExp + MARKER_NEWER_THRESHOLD_SEC) return false;

      // This tab's in-memory token is now older than the cookie — drop it so sessionExp and
      // logout() follow the fresh cookie session instead.
      this.accessToken = null;
      this.sessionExpiresAt = markerExp;
      // Another tab's refresh succeeded — a pending transient-failure retry would only waste
      // one of the limited refreshes.
      this._clearSessionRetry();
      this._scheduleProactiveRefresh();
      return true;
    },

    /**
     * SV-563: when the tab becomes visible again, adopt a newer marker (another tab refreshed),
     * or re-check the session if the marker has disappeared (logged out in another tab, or it
     * expired while the laptop slept). Only treats a missing marker as meaningful once this
     * page has seen one, so environments without the marker don't re-check on every focus.
     */
    _onVisibilityChange() {
      if (document.visibilityState !== 'visible' || !this.isAuthenticated) return;
      if (this._adoptNewerSessionMarker()) return;
      if (_sessionMarkerSeen && this.hasActiveSession && readSessionExpCookie() === null) {
        void this.validateSession();
      }
    },

    /** SV-563: register the visibilitychange listener once per page. */
    _watchSessionMarker() {
      if (_visibilityListenerRegistered) return;
      _visibilityListenerRegistered = true;
      // Resolve the store at event time (not a captured `this`) so it's always the active one.
      document.addEventListener('visibilitychange', () => useAuthStore()._onVisibilityChange());
    },

    /** Cancel any pending proactive refresh timer. */
    _clearRefreshTimer() {
      if (_refreshTimerId) {
        clearTimeout(_refreshTimerId);
        _refreshTimerId = null;
      }
    },

    /**
     * Re-hydrate runtime config from persisted store state and re-schedule
     * the proactive refresh timer after a page reload.
     *
     * Call once on app startup after Pinia is initialised so that
     * `config.personId` is correct and the refresh timer is running.
     */
    initAuthConfig(): void {
      if (this.personId) {
        setAuthContext({ personId: this.personId });
      }
      this._watchSessionMarker();
      if (this.accessToken) {
        this._scheduleProactiveRefresh();
      } else if (this.user?.id) {
        // A laptop locked/asleep (or the tab fully closed) across a gap longer than the idle
        // timeout means the in-memory idle timer in App.vue never got a chance to fire — force
        // a fresh login instead of silently resuming via the refresh cookie.
        if (hasIdleTimeoutElapsed()) {
          // Go through the real logout() (not just clearAuth()) so the httpOnly refresh
          // cookie is actually invalidated server-side via logoutSession()'s cookie-fallback
          // /auth/logout call — otherwise the "logged out" browser would still hold a valid
          // refresh credential despite appearing logged out locally.
          void this.logout();
        } else {
          // PHX-1180: accessToken/refreshToken are no longer persisted, so a page
          // reload leaves them null in memory even though the httpOnly cookies may
          // still be valid. SV-563: validate the access cookie instead of calling the
          // rate-limited /auth/refresh on every reload — validateSession() only falls
          // back to a refresh when the access cookie has actually expired.
          void this.validateSession();
        }
      }
    },
  },

  persist: {
    paths: [
      'user',
      // PHX-1180: accessToken/refreshToken/mfaSetupAccessToken are intentionally
      // NOT persisted — the real session now lives in an httpOnly cookie; these
      // fields are in-memory-only for the lifetime of the tab.
      'personId',
      'timeZone',
      'challengeData',
      'pendingEmail',
      'mfaSetupRequired',
      'mfaChallengeType',
      'totpQrData',
      'loginErrorCode',
    ],
  },
});
